import React, { useContext, type ReactNode } from "react";
import type * as H from "history";
import { Route, Redirect, type RouteProps } from "react-router-dom";
import { AuthContext } from "../Auth/AuthContext";
import { setPostLoginRedirect } from "../Auth/ssoSession";
import { ModalProvider } from "@SeemplicityDev/shared";

interface ProtectedRouteProps extends Omit<RouteProps, "location"> {
  children?: ReactNode;
  // When rendered inside a <Switch>, react-router v5 injects a full Location
  // object as the `location` prop (RouteProps types it loosely as string | object).
  location?: H.Location;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  path,
  ...rest
}) => {
  // AuthContext is being migrated in parallel; type the consumed slice locally.
  const { authState } = useContext(AuthContext) as {
    authState: { authenticated?: boolean };
  };

  if (!authState.authenticated) {
    const redirectUrl = rest.location?.pathname;
    if (redirectUrl && redirectUrl !== "/dashboard") {
      // Route the write through the validated helper so an unsafe path (e.g. a
      // protocol-relative "//evil.com") can never reach storage and be replayed
      // as a post-login redirect.
      setPostLoginRedirect(
        `${rest.location?.pathname ?? ""}${rest.location?.search ?? ""}`
      );
    }
    return <Redirect to="/login" />;
  }

  return (
    <Route
      path={path}
      {...rest}
      render={() => <ModalProvider>{children}</ModalProvider>}
    />
  );
};

export default ProtectedRoute;
